import { useEffect, useMemo, useRef } from 'react';
import GlobalNavigation from './GlobalNavigation.js';
import FloatingReservationButton from './FloatingReservationButton.js';
import SeoHelmet from './SeoHelmet.js';
import { getSeoByPath } from '../seoConfig.js';
import './DocPage.css';

const DocPageLayout = ({
    currentRoute,
    pageLabel,
    pageTitle,
    heroImage,
    heroImageAlt,
    heroHighlight,
    heroDescription,
    children,
    showFloatingToggle = false,
    brandTitle,
    brandSubtitle,
    heroPadding = 'default',
    seo: seoOverrides
}) => {
    const pageEndRef = useRef(null);
    const seoMeta = useMemo(() => {
        const baseSeo = getSeoByPath(currentRoute);

        if (!seoOverrides) {
            return baseSeo;
        }

        return {
            ...baseSeo,
            ...seoOverrides
        };
    }, [currentRoute, seoOverrides]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [currentRoute]);

    const heroClassName = `doc-hero${heroPadding === 'compact' ? ' doc-hero--compact' : ''}`;

    return (
        <div className="doc-page">
            <SeoHelmet {...seoMeta} />
            <section className={heroClassName}>
                <div className="hero-bubble hero-bubble--large" />
                <div className="hero-bubble hero-bubble--medium" />
                <div className="hero-bubble hero-bubble--small" />

                <GlobalNavigation
                    currentRoute={currentRoute}
                    includeSections={false}
                    showFloatingToggle={showFloatingToggle}
                    brandTitle={brandTitle}
                    brandSubtitle={brandSubtitle}
                />

                <div className="doc-hero__grid">
                    <div className="doc-hero__copy">
                        {pageLabel && <span className="doc-hero__label">{pageLabel}</span>}
                        {pageTitle && <h2 className="doc-hero__title">{pageTitle}</h2>}
                        {heroHighlight && <p className="doc-hero__highlight">{heroHighlight}</p>}
                        {heroDescription && (
                            <div className="doc-hero__description">
                                {Array.isArray(heroDescription)
                                    ? heroDescription.map((paragraph, index) => (
                                          <p key={index}>{paragraph}</p>
                                      ))
                                    : <p>{heroDescription}</p>}
                            </div>
                        )}
                    </div>
                    {heroImage && (
                        <div className="doc-hero__image">
                            <img src={heroImage} alt={heroImageAlt} loading="lazy" />
                        </div>
                    )}
                </div>
            </section>

            <main className="doc-content">{children}</main>
            <div ref={pageEndRef} className="doc-page__sentinel" />
            <FloatingReservationButton intersectionTargetRef={pageEndRef} />
        </div>
    );
};

export default DocPageLayout;
